// @ts-check
// Página Novidades em /release-notes (contrato 08-contrato-novidades.md, seção 8): todas as versões, a mais nova
// primeiro, com data, resumo e destaques; cada destaque leva à tela quando o papel pode abri-la. Cada versão tem âncora
// (#v1-13-0): os links do índice empilham no histórico, então o voltar do navegador percorre as versões e sai da
// página. O catálogo está no bundle, então a página é igual no modo demonstração e na homologação.
import { html } from 'htm/preact';
import { useEffect, useRef } from 'preact/hooks';
import { Link, locationState } from '../../app/router.js';
import { hasCurrentPermissionFlag, isCoordinationFocusedState, isTenantModuleEnabled, navigationItemsState, objectsById, userObjectsState } from '../../data/metadata-store.js';
import { markReleaseNotesSeen } from '../../data/release-notes.js';
import { APP_VERSION } from '../../../../shared/app-version.js';
import { RELEASE_NOTES, releaseNoteAnchorId, releaseNoteTextKey } from '../../../../shared/release-notes/release-notes.js';
import { resolveReleaseNoteLink } from '../../lib/release-notes-links.js';
import { appEnvironmentState } from '../../lib/app-environment.js';
import { formatDate } from '../../lib/format.js';
import { Icon } from '../../ui/icon.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { releaseNotesAnnouncementState } from './release-notes-state.js';
import { t } from '../../i18n/index.js';

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/** Mesmo contexto dos atalhos G + letra: o que o papel lê, o menu e as flags. */
function useReleaseNoteLinkContext() {
  return {
    hasPermissionFlag: hasCurrentPermissionFlag,
    readableObjectNames: new Set(userObjectsState.value.map((objectMetadata) => objectMetadata.nameSingular)),
    navigationItems: navigationItemsState.value.map((item) => ({
      href: item.href,
      objectNameSingular: item.objectMetadataId ? objectsById.value.get(item.objectMetadataId)?.nameSingular ?? null : null,
    })),
    isCoordinationFocused: isCoordinationFocusedState.value,
    isModuleEnabled: isTenantModuleEnabled,
  };
}

/**
 * @param {{ entry: import('../../../../shared/release-notes/release-notes.js').ReleaseNote,
 *   linkContext: import('../../lib/release-notes-links.js').ReleaseNoteLinkContext }} props
 */
function ReleaseNoteVersion({ entry, linkContext }) {
  const anchorId = releaseNoteAnchorId(entry.version);
  const titleId = `${anchorId}-title`;
  const textKey = `releaseNotes.versions.${releaseNoteTextKey(entry.version)}`;
  const isCurrent = entry.version === APP_VERSION;
  return html`<article class="release-notes-version" id=${anchorId} aria-labelledby=${titleId}>
    <header class="release-notes-version-header">
      <h2 class="release-notes-version-title" id=${titleId} tabindex="-1">
        <${Link} href=${`/release-notes#${anchorId}`} className="release-notes-version-anchor">
          ${t('releaseNotes.page.versionTitle', { version: entry.version })}
        <//>
      </h2>
      ${isCurrent ? html`<span class="release-notes-chip">${t('releaseNotes.page.currentBadge')}</span>` : null}
      <time class="release-notes-version-date" datetime=${entry.publishedOn}>${t('releaseNotes.page.publishedOn', { date: formatDate(entry.publishedOn) })}</time>
    </header>
    <p class="release-notes-version-summary">${t(`${textKey}.summary`)}</p>
    <ul class="release-notes-highlights" aria-label=${t('releaseNotes.page.highlightsLabel', { version: entry.version })}>
      ${entry.highlights.map((highlight) => {
        const link = resolveReleaseNoteLink(highlight.link, linkContext);
        return html`<li class="release-notes-highlight" key=${highlight.key}>
          <h3 class="release-notes-highlight-title">${t(`${textKey}.highlights.${highlight.key}.title`)}</h3>
          <p class="release-notes-highlight-description">${t(`${textKey}.highlights.${highlight.key}.description`)}</p>
          ${link
            ? html`<${Link} href=${link.href} className="release-notes-highlight-link">
                <span>${t(link.labelKey)}</span><${Icon} name="IconArrowRight" size=${14} />
              <//>`
            : null}
        </li>`;
      })}
    </ul>
  </article>`;
}

export function ReleaseNotesPage() {
  const bodyRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const isFirstRenderRef = useRef(true);
  const hash = locationState.value.hash;
  const linkContext = useReleaseNoteLinkContext();

  useEffect(() => {
    document.title = t('releaseNotes.page.title');
    // abrir a página com o aviso pendente conta como visto
    if (releaseNotesAnnouncementState.peek()) markReleaseNotesSeen();
  }, []);

  // âncora da versão: rola até ela e, quando a chegada é pelo link, leva o foco ao título; sem âncora (voltar até a
  // entrada da página), volta ao topo
  useEffect(() => {
    const anchorId = decodeURIComponent(hash.replace(/^#/, ''));
    const isFirstRender = isFirstRenderRef.current;
    isFirstRenderRef.current = false;
    const frame = requestAnimationFrame(() => {
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      if (!anchorId) {
        if (!isFirstRender) bodyRef.current?.scrollTo({ top: 0, behavior });
        return;
      }
      const target = document.getElementById(anchorId);
      if (!target) return;
      target.scrollIntoView({ block: 'start', behavior });
      target.querySelector('h2')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  const environment = appEnvironmentState.value;
  return html`<${PageCard} header=${html`<${PageHeader} icon="IconSparkles" title=${t('releaseNotes.page.title')} />`}>
    <div class="settings-page release-notes-page" ref=${bodyRef} data-testid="release-notes-page">
      <h1 class="release-notes-visually-hidden">${t('releaseNotes.page.title')}</h1>
      <div class="release-notes-intro">
        <p>${t('releaseNotes.page.intro')}</p>
        <p class="release-notes-current-version">
          <span>${t('releaseNotes.page.currentVersion', { version: APP_VERSION })}</span>
          ${environment !== 'production'
            ? html`<span class="app-environment-badge" data-environment=${environment}>${t(`navigation.environment.${environment}`)}</span>`
            : null}
        </p>
      </div>
      <nav class="release-notes-index" aria-label=${t('releaseNotes.page.versionsNavLabel')}>
        ${RELEASE_NOTES.map((entry) => html`<${Link}
          key=${entry.version}
          href=${`/release-notes#${releaseNoteAnchorId(entry.version)}`}
          className="release-notes-index-link"
          aria-current=${hash === `#${releaseNoteAnchorId(entry.version)}` ? 'location' : undefined}
        >${entry.version}<//>`)}
      </nav>
      ${RELEASE_NOTES.map((entry) => html`<${ReleaseNoteVersion} key=${entry.version} entry=${entry} linkContext=${linkContext} />`)}
    </div>
  <//>`;
}
